import { PCE_DDA_STATUS, SOURCE_TYPE_CATEGORIES } from "@core/constants"

/**
 * Paramètres API pour les filtres de sources (valeurs attendues par l'API métier)
 */
export type SourceFilterApiParams = {
    /**
     * Type(s) de source(s) à filtrer (ex. pdl, pce, iot)
     */
    idtype?: string[]
    /**
     * Niveau(s) de batterie à filtrer (codes API : 0-5, 5=100%, 0=EOL)
     */
    battery?: string | string[]
    /**
     * Localisation à filtrer (codes API : 1=fixed, 0=moved)
     */
    location?: string | string[]
    /**
     * État de broadcast à filtrer (codes API : 2=ok, 1=inLoss, 0=ko, -1=neverEmit)
     */
    broadcast?: string | string[]
    /**
     * État(s) du droit d'accès à filtrer (pour PCE / pceKo)
     */
    etat_droit_acces?: string | string[]
    /**
     * Droits à filtrer (views pour total_billing_source)
     */
    rights?: string | string[]
    /**
     * Uniquement les sources liées à une demande de consentement en cours
     */
    consent_pending_only?: boolean
}

/**
 * Mapping des filtres UI vers les paramètres API (aligné sur homeys-legacy)
 * Chaque clé de filtre mappe vers un objet { paramApi: [valeurs] }
 */
const FILTER_MAPPINGS: Record<
    string,
    Partial<Record<keyof SourceFilterApiParams, string[]>>
> = {
    elec: { idtype: [...SOURCE_TYPE_CATEGORIES.ELEC.types] },
    gaz: { idtype: [...SOURCE_TYPE_CATEGORIES.GAZ.types] },
    rcu: { idtype: [...SOURCE_TYPE_CATEGORIES.RCU.types] },
    eau: { idtype: [...SOURCE_TYPE_CATEGORIES.EAU.types] },
    capteur: { idtype: ["iot"] },
    fixed: { location: ["1"] },
    moved: { location: ["0"] },
    battery100: { battery: ["5"] },
    battery75: { battery: ["4"] },
    battery50: { battery: ["3"] },
    battery25: { battery: ["2"] },
    battery0: { battery: ["1"] },
    batteryEOL: { battery: ["0"] },
    ok: { broadcast: ["2"] },
    inLoss: { broadcast: ["1"] },
    ko: { broadcast: ["0"] },
    neverEmit: { broadcast: ["-1"] },
    total_billing_source: { rights: ["views"] },
    pceKo: {
        etat_droit_acces: [
            PCE_DDA_STATUS.A_VALIDER,
            PCE_DDA_STATUS.OBSOLETE,
            PCE_DDA_STATUS.REFUSED,
            PCE_DDA_STATUS.REVOKED,
            PCE_DDA_STATUS.RECHECK,
            PCE_DDA_STATUS.ERROR,
        ],
    },
}

/**
 * Mappe les filtres UI vers les paramètres API
 * Agrège les valeurs par clé comme dans homeys-legacy (getQueryParams)
 * @param filters - Tableau de filtres UI (elec, gaz, rcu, eau, capteur, ok, inLoss, etc.)
 * @returns Paramètres API (idtype, battery, location, broadcast, etat_droit_acces, rights)
 */
export function mapFiltersToApiParams(
    filters: string[]
): SourceFilterApiParams {
    const params: Record<string, string[]> = {}

    filters.forEach((filter) => {
        const mapping = FILTER_MAPPINGS[filter]
        if (mapping) {
            for (const key of Object.keys(
                mapping
            ) as (keyof SourceFilterApiParams)[]) {
                const values = mapping[key]
                if (values) {
                    params[key] = [...(params[key] ?? []), ...values]
                }
            }
        }
    })

    const result: SourceFilterApiParams = {}
    if (params.idtype?.length) result.idtype = params.idtype
    if (params.battery?.length)
        result.battery =
            params.battery.length === 1 ? params.battery[0]! : params.battery
    if (params.location?.length)
        result.location =
            params.location.length === 1 ? params.location[0]! : params.location
    if (params.broadcast?.length)
        result.broadcast =
            params.broadcast.length === 1
                ? params.broadcast[0]!
                : params.broadcast
    if (params.etat_droit_acces?.length)
        result.etat_droit_acces =
            params.etat_droit_acces.length === 1
                ? params.etat_droit_acces[0]!
                : params.etat_droit_acces
    if (params.rights?.length)
        result.rights =
            params.rights.length === 1 ? params.rights[0]! : params.rights
    if (filters.includes("consentRequestPending")) {
        result.consent_pending_only = true
    }

    return result
}
